/**
 * EditFieldDialog — shadcn-styled replacement for window.prompt() when
 * editing a single text field. Built for the "click pencil → edit URL →
 * save" pattern that was using browser-native prompt() in CompanyDetail
 * and ClientDetail.
 *
 * Supports single-line (Input) and multi-line (Textarea) variants. Runs
 * the caller's onSave in a loading state so the confirm button shows a
 * spinner while the PATCH is in flight, and auto-closes on success.
 * Failures are the caller's responsibility — they should toast.error
 * and return false from onSave to keep the dialog open for retry.
 */

import { useEffect, useId, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";

interface EditFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  label: string;
  placeholder?: string;
  initialValue?: string;
  /** "text" = Input, "textarea" = Textarea. Default text. */
  type?: "text" | "textarea";
  /** Confirm button label. Default "Save". */
  saveLabel?: string;
  /**
   * Called with the trimmed new value when the user clicks Save. If you
   * return false (explicitly), the dialog stays open (for retry). Any
   * other return (or throw caught by the caller) closes the dialog.
   */
  onSave: (value: string) => void | boolean | Promise<void | boolean>;
}

export function EditFieldDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  placeholder,
  initialValue = "",
  type = "text",
  saveLabel = "Save",
  onSave,
}: EditFieldDialogProps) {
  const fieldId = useId();
  const [error, setError] = useState("");
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  // Reset draft value when the dialog (re)opens so stale input from a
  // previous open doesn't leak in.
  useEffect(() => {
    if (open) { setValue(initialValue); setError(""); }
  }, [open]);

  const handleSave = async () => {
    if (saving) return;
    const trimmed = value.trim();
    setSaving(true);
    setError("");
    try {
      const result = await onSave(trimmed);
      if (result !== false) {
        onOpenChange(false);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter submits for single-line inputs; Textarea uses Enter for
    // newlines so we don't trap it.
    if (type === "text" && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2">
          <label htmlFor={fieldId} className="text-xs font-medium text-muted-foreground">{label}</label>
          {type === "textarea" ? (
            <Textarea
              id={fieldId}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              disabled={saving}
              rows={4}
              autoFocus
            />
          ) : (
            <Input
              id={fieldId}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={saving}
              autoFocus
            />
          )}
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              saveLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
